import React, { useRef, useState, useEffect } from "react";
import Layout from 'Layout'
import { Pagination } from "antd";
import './styles.scss'
import {
  Table,
  Input,
  Select,
  Button,
  Space,
  Tag,
  Dropdown,
  Breadcrumb,
  message,
} from "antd";
import type { ColumnsType, ColumnType } from "antd/es/table";
import type { InputRef } from "antd";
import { useNavigate } from "react-router-dom";
import { mixGetV1 } from "services/mixin";
import {
  SearchOutlined,
  ExportOutlined,
  MoreOutlined,
  EyeOutlined,
  DeleteOutlined,
  HomeOutlined,
  RightOutlined,
} from "@ant-design/icons";
import type { FilterDropdownProps } from "antd/es/table/interface";

const { Option } = Select;

interface Partner {
  key: string;
  name: string;
  phone: string;
  email: string;
  status: "Đang hoạt động" | "Hết hạn" | "Chưa duyệt";
  affiliateCode: number;
  affiliateLink: number;
  affiliateAll: number;
  commission: number;
  revenue: number;
}

const sampleData: Partner[] = [
  {
    key: "1",
    name: "Nguyễn Văn A",
    phone: "0963258741",
    email: "a.test@gmail.com",
    status: "Đang hoạt động",
    affiliateCode: 2,
    affiliateLink: 5,
    affiliateAll: 7,
    commission: 120000,
    revenue: 3000000,
  },
  {
    key: "2",
    name: "Trần Thị B",
    phone: "0987654321",
    email: "b.test@gmail.com",
    status: "Hết hạn",
    affiliateCode: 0,
    affiliateLink: 1,
    affiliateAll: 1,
    commission: 0,
    revenue: 0,
  },
  {
    key: "3",
    name: "Hoàng C",
    phone: "0912345678",
    email: "c.test@gmail.com",
    status: "Chưa duyệt",
    affiliateCode: 1,
    affiliateLink: 0,
    affiliateAll: 1,
    commission: 50000,
    revenue: 1000000,
  },
  // ... thêm cho đủ total nếu cần demo
];

const TOTAL_ENTRIES = 83; // giả sử dataset tổng là 83 (theo hình mẫu). Bạn có thể thay bằng total từ API.

const PartnerList: React.FC = () => {
  const navigate = useNavigate();
  // const [data] = useState<Partner[]>(sampleData);
  const [pageSize, setPageSize] = useState<number>(10);
  const [searchText, setSearchText] = useState<string>("");
  const searchInputRef = useRef<InputRef | null>(null);
  const [data, setData] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [statusFilter, setStatusFilter] = useState<string>("all");


  const handleView = (record: Partner) => {
    message.info(`Xem chi tiết: ${record.name} (${record.email})`);
    navigate(`/doi-tac/chi-tiet/${record.key}`);
  };

  const displayedData = searchText
  ? data.filter(
      (r) =>
        r.name.toLowerCase().includes(searchText.toLowerCase()) ||
        r.phone.toLowerCase().includes(searchText.toLowerCase()) ||
        r.email.toLowerCase().includes(searchText.toLowerCase())
    )
  : data;

  const filteredData = displayedData.filter((item) => {
    if (statusFilter === "all") return true;
    if (statusFilter === "active") return item.status === "Đang hoạt động";
    if (statusFilter === "pending") return item.status === "Chưa duyệt";
    if (statusFilter === "expired") return item.status === "Hết hạn";
    return true;
  });

  const getData = async (page = 1, limit = 10) => {
    try {
      setLoading(true);
      const res = await mixGetV1(`/api/v1/be/contact_affiliates?custom_page=${page}&custom_limit=${limit}&custom_sort=id,desc`);
      if (res.status) {
        const items = res.data.items || [];
        setTotal(res.data.total || 0);

        // Map dữ liệu API sang định dạng bảng
        const mapped = items.map((item: any) => ({
          key: item.id,
          name: item.customer?.fullname || "N/A",
          phone: item.customer?.phone || "-",
          email: item.customer?.email || "-",
          status:
            item.status === 1
              ? "Đang hoạt động"
              : item.status === 0
              ? "Chưa duyệt"
                : (new Date().getTime() - new Date(item.created_at).getTime()) / (1000*60*60*24) > 15
                  ? "Hết hạn"
                  : "Chưa duyệt",
          affiliateCode: item.code,
          created_at: item.created_at,
        }));

        setData(mapped);
      } else {
        message.error("Lấy danh sách thất bại!");
      }
    } catch (error) {
      console.error(error);
      message.error("Không thể kết nối máy chủ!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData(currentPage, pageSize);
  }, [currentPage, pageSize]);

  const handleDelete = (record: Partner) => {
    message.success(`Đã xóa: ${record.name}`);
    // TODO: logic xóa + refresh data
  };

  const renderStatusTag = (status: Partner["status"]) => {
    const color = status === "Đang hoạt động" ? "green" : status === "Chưa duyệt" ? "orange" : "gray";
    return <Tag color={color}>{status}</Tag>;
  };

  const getColumnSearchProps = <T extends object>(dataIndex: keyof T): ColumnType<T> => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: FilterDropdownProps) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={(node) => {
            // @ts-ignore
            searchInputRef.current = node;
          }}
          placeholder={`Tìm ${String(dataIndex)}`}
          value={selectedKeys?.[0] as string}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => {
            confirm();
            setSearchText((selectedKeys?.[0] as string) || "");
          }}
          style={{ marginBottom: 8, display: "block", width: 188 }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => {
              confirm();
              setSearchText((selectedKeys?.[0] as string) || "");
            }}
            size="small"
          >
            Search
          </Button>
          <Button
            onClick={() => {
              clearFilters && clearFilters();
              setSearchText("");
              confirm();
            }}
            size="small"
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />,
    onFilter: (value: boolean | React.Key, record: T) => {
      const recordValue = (record as any)[dataIndex];
      if (recordValue === undefined || recordValue === null) return false;
      return String(recordValue).toLowerCase().includes(String(value).toLowerCase());
    },
  });

  const columns: ColumnsType<Partner> = [
    {
      title: "Họ và tên",
      width: 180,
      dataIndex: "name",
      key: "name",
      ...getColumnSearchProps<Partner>("name"),
      render: (text: string ,record: any) => <a href={`/doi-tac/chi-tiet/${record.key}`} style={{ color: "#6e6b7b" }}>{text}</a>,
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Số điện thoại",
      width: 180,
      dataIndex: "phone",
      key: "phone",
      ...getColumnSearchProps<Partner>("phone"),
      sorter: (a, b) => a.phone.localeCompare(b.phone),
    },
    {
      title: "Email",
      width: 180,
      dataIndex: "email",
      key: "email",
      ...getColumnSearchProps<Partner>("email"),
      sorter: (a, b) => a.email.localeCompare(b.email),
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      key: "status",
      filters: [
        { text: "Đang hoạt động", value: "Đang hoạt động" },
        { text: "Hết hạn", value: "Hết hạn" },
        { text: "Chưa duyệt", value: "Chưa duyệt" },
      ],
      onFilter: (value, record) => record.status === value,
      render: (status) => renderStatusTag(status),
      width: 140,
    },
    {
      title: "Affiliate Code",
      width: 140,
      dataIndex: "affiliateCode",
      key: "affiliateCode",
      sorter: (a, b) => a.affiliateCode - b.affiliateCode,
      align: "center",
    },
    {
      title: "Affiliate Link",
      width: 160,
      dataIndex: "affiliateLink",
      key: "affiliateLink",
      sorter: (a, b) => a.affiliateLink - b.affiliateLink,
      align: "center",
    },
    {
      title: "Affiliate All",
      width: 140,
      dataIndex: "affiliateAll",
      key: "affiliateAll",
      sorter: (a, b) => a.affiliateAll - b.affiliateAll,
      align: "center",
    },
    {
      title: "Tổng hoa hồng",
      width: 150,
      ellipsis: true,
      dataIndex: "commission",
      key: "commission",
      sorter: (a, b) => a.commission - b.commission,
      align: "right",
      render: (val: number | undefined) => (
        <span style={{ color: "#6e6b7b" }}>
          {Number(val || 0).toLocaleString("vi-VN")}
        </span>
      ),
    },
    {
      title: "Tổng doanh thu",
      ellipsis: true,
      width: 150,
      dataIndex: "revenue",
      key: "revenue",
      sorter: (a, b) => a.revenue - b.revenue,
      align: "right",
      render: (val: number | undefined) => (
        <span style={{ color: "#6e6b7b" }}>
          {Number(val || 0).toLocaleString("vi-VN")}
        </span>
      ),
    },
    {
      title: "Actions",
      width: 80,
      key: "actions",
      align: "center",
      render: (_, record) => {
        const items = [
          {
            key: "view",
            label: (
              <span onClick={() => handleView(record)} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <EyeOutlined /> Xem chi tiết
              </span>
            ),
          },
          {
            key: "delete",
            label: (
              <span onClick={() => handleDelete(record)} style={{ display: "flex", alignItems: "center", gap: 8, color: "red" }}>
                <DeleteOutlined /> Xóa
              </span>
            ),
          },
        ];

        return (
          <Dropdown menu={{ items }} trigger={["click"]} placement="bottomRight">
            <Button type="text" icon={<MoreOutlined style={{ fontSize: 18 }} />} />
          </Dropdown>
        );
      },
    },
  ];

  // compute displayed data (demo): use searchText quick filter for client-side
  // const displayedData = searchText
  //   ? data.filter(
  //     (r) =>
  //       r.name.toLowerCase().includes(searchText.toLowerCase()) ||
  //       r.phone.toLowerCase().includes(searchText.toLowerCase()) ||
  //       r.email.toLowerCase().includes(searchText.toLowerCase())
  //   )
  //   : data;

  // For demo "total" (the text "Showing 1 to 10 of 83 entries"), we use TOTAL_ENTRIES,
  // but the actual rows shown in table = displayedData.
  // const total = TOTAL_ENTRIES;
  // const [currentPage, setCurrentPage] = useState(1);

  const start = (currentPage - 1) * pageSize + 1;
  const end = Math.min(total, currentPage * pageSize);
  return (
    <Layout>
      <div className="partners-page">
        <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <h2 style={{ fontWeight: 600, fontSize: 24, marginBottom: 0, color: "#6e6b7b", borderRight: "1px solid #d6dce1", paddingRight: 12 }}>
            Đối tác
          </h2>
          <Breadcrumb
            separator={<RightOutlined style={{ fontSize: 12, color: "#6e6b7b" }} />}
            items={[
              {
                title: (
                  <a href="#">
                    <HomeOutlined style={{ color: "#096dd9", fontSize: 16 }} />
                  </a>
                ),
              },
              { title: "Danh sách" },
            ]}
          />
        </div>

        <div className="card-like" style={{ borderRadius: 8, border: "1px solid #f0f0f0", background: "#fff" }}>
          {/* Filters top bar */}
          <div style={{ padding: 16, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <Select
              defaultValue="10"
              style={{ width: 80, height: 38 }}
              onChange={(v) => {
                setPageSize(Number(v));
                setCurrentPage(1);
              }}
              className="control-38"
            >
              <Option value="10">10</Option>
              <Option value="20">20</Option>
              <Option value="50">50</Option>
            </Select>

            <Select defaultValue="all"  onChange={(v) => {setStatusFilter(v);setCurrentPage(1);}}style={{ width: 150, height: 38  }} className="control-38">
              <Option value="all">Xem tất cả</Option>
              <Option value="active">Đang hoạt động</Option>
              <Option value="expired">Hết hạn</Option>
              <Option value="pending">Chưa duyệt</Option>
            </Select>

            <Input
              placeholder="Tìm nhanh theo tên , email"
              prefix={<SearchOutlined />}
              style={{ width: 320, height: 38 }}
              onPressEnter={(e) => {
                const v = (e.target as HTMLInputElement).value.trim();
                setSearchText(v);
                setCurrentPage(1);
              }}
              className="control-38"
            />

            <Button className="cl-white" type="primary" icon={<ExportOutlined style={{ color: "#fff" }} />} style={{ marginLeft: "auto", height: 38 }}>
              Export
            </Button>
          </div>

          {/* Table */}
          <Table
            className="custom-table"
            columns={columns}
            // dataSource={displayedData}
            dataSource={filteredData}
            pagination={false}
            rowKey="key"
            size="middle"
            bordered={false}
            scroll={{ x: '1200px' }}
          />

          {/* Pagination area: left info + right pagination */}
          <div className="partners-pagination" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 16, padding: '0 16px 16px 16px' }}>
            <div className="info-text">
              Showing {start} to {end} of {total} entries
            </div>

            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={total}
              onChange={(page) => setCurrentPage(page)}
              showSizeChanger={false}
              className="custom-pagination"
            />
          </div>
        </div>
      </div>
    </Layout >
  );
};

export default PartnerList;
